import { z } from 'zod';
import { isIndustryCode, legacyIndustryLabelToCode, type IndustryCode } from '@leanin/shared';
import { stringList } from '../filters/helpers';

export type PeopleSort = 'recommended' | 'recent' | 'name';
const SCOPE_VALUES = ['all', 'circle', 'network'] as const;
export type PeopleScope = (typeof SCOPE_VALUES)[number];

// A malformed value drops that one filter, never the whole URL.
const text = z.string().optional().catch(undefined);
const list = z.union([z.string(), z.array(z.string())]).optional().catch(undefined);

function industryCodes(raw: string | string[] | undefined): IndustryCode[] {
  const codes = stringList(raw).flatMap((v) => {
    const code = isIndustryCode(v) ? v : legacyIndustryLabelToCode(v);
    return code === null ? [] : [code];
  });
  return [...new Set(codes)];
}

export const peopleFilterSchema = z
  .object({
    q: text,
    scope: z.enum(SCOPE_VALUES).optional().catch(undefined),
    sort: z.enum(['recommended', 'recent', 'name']).optional().catch(undefined),
    networkId: text,
    city: text,
    loc: list,
    // Structured location picks from the typeahead: `st` = US-state USPS
    // codes (multi), `cn` = one ISO country code. Codes only — display
    // names come from location-labels.ts.
    st: list,
    cn: text,
    net: list,
    // Codes. A bookmark from before the vocabulary carries one of the legacy
    // English labels, which maps to its code; anything else is dropped rather
    // than sent to the API.
    industry: list,
    career: list,
    openTo: list,
    community: list,
  })
  .transform((v) => ({
    q: v.q ?? '',
    scope: v.scope ?? ('all' as PeopleScope),
    sort: v.sort ?? ('recommended' as PeopleSort),
    networkId: v.networkId ?? '',
    city: v.city ?? '',
    loc: stringList(v.loc),
    st: stringList(v.st),
    cn: v.cn ?? '',
    net: stringList(v.net),
    industry: industryCodes(v.industry),
    career: stringList(v.career),
    openTo: stringList(v.openTo),
    community: stringList(v.community),
  }));

export type PeopleFilters = z.infer<typeof peopleFilterSchema>;

export const PEOPLE_FILTER_DEFAULTS: PeopleFilters = {
  q: '',
  scope: 'all',
  sort: 'recommended',
  networkId: '',
  city: '',
  loc: [],
  st: [],
  cn: '',
  net: [],
  industry: [],
  career: [],
  openTo: [],
  community: [],
};

export type PeopleSearch = Partial<PeopleFilters>;

// The route's search state: the parsed filters minus anything at its
// default, so the URL and links only carry what the viewer chose.
export function parsePeopleSearch(raw: Record<string, unknown>): PeopleSearch {
  const p = peopleFilterSchema.parse(raw);
  const d = PEOPLE_FILTER_DEFAULTS;
  return {
    ...(p.q !== d.q ? { q: p.q } : {}),
    ...(p.scope !== d.scope ? { scope: p.scope } : {}),
    ...(p.sort !== d.sort ? { sort: p.sort } : {}),
    ...(p.networkId !== d.networkId ? { networkId: p.networkId } : {}),
    ...(p.city !== d.city ? { city: p.city } : {}),
    ...(p.loc.length > 0 ? { loc: p.loc } : {}),
    ...(p.st.length > 0 ? { st: p.st } : {}),
    ...(p.cn !== d.cn ? { cn: p.cn } : {}),
    ...(p.net.length > 0 ? { net: p.net } : {}),
    ...(p.industry.length > 0 ? { industry: p.industry } : {}),
    ...(p.career.length > 0 ? { career: p.career } : {}),
    ...(p.openTo.length > 0 ? { openTo: p.openTo } : {}),
    ...(p.community.length > 0 ? { community: p.community } : {}),
  };
}
